import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"
import { createLogger } from "../lib/logger"
import { getI18nMessage } from "../lib/i18n_helper"
import { showError, showSuccess } from "../lib/platform_toast"
import { NativeBridge } from "../lib/native_bridge"
import { prefersReducedMotion } from "../lib/motion"

// Connects to data-controller="direct-upload"
// Handles Active Storage Direct Upload with image preview
// Supports both Web (file input) and Native (NativeBridge.Camera) modes
// Supports drag-and-drop reordering via SortableJS
const log = createLogger('DirectUpload')

export default class extends Controller {
  static targets = ["input", "preview", "progress", "count", "addButton"]
  static values = {
    url: String,
    modelName: { type: String, default: 'product' }, // Model name for deletion field (ad, product, etc.)
    maxFiles: { type: Number, default: 10 },
    // Default fallback: BusinessConstants::IMAGE_UPLOAD_MAX_SIZE (5MB)
    // Always pass via data-direct-upload-max-size-value for SSOT compliance
    maxSize: { type: Number, default: 5 * 1024 * 1024 },
    allowedFormats: { type: String, default: 'jpg,jpeg,png,webp' },
    sortable: { type: Boolean, default: false } // Enable drag-and-drop reordering
  }

  connect() {
    this.uploadedFiles = []
    // Active Storage direct upload URL
    this.urlValue = this.urlValue || "/rails/active_storage/direct_uploads"

    // Detect native app environment
    // iOS: Use standard file input (system native file picker UI)
    // Android: Use NativeBridge.Camera (custom implementation)
    this.isNative = NativeBridge.isAndroid()
    if (this.isNative) {
      log.info("Android app detected, using NativeBridge.Camera for image selection")
    }

    // Sync existing images (for edit mode)
    this.syncExistingImages()
    this.updateCount()

    // Disable button if max files reached
    if (this.uploadedFiles.length >= this.maxFilesValue) {
      this.disableAddButton()
    }

    // Initialize SortableJS for drag-and-drop reordering
    if (this.sortableValue && this.hasPreviewTarget) {
      this.initSortable()
    }
  }

  /**
   * Initialize SortableJS for drag-and-drop image reordering
   * Touch-friendly drag animation with primary badge update.
   * sortablejs is imported lazily so it stays out of the eager application.js entry — it only loads on
   * upload forms that actually enable reordering (data-direct-upload-sortable-value="true").
   */
  async initSortable() {
    const { default: Sortable } = await import("sortablejs")
    this.sortableInstance = Sortable.create(this.previewTarget, {
      animation: 150,
      ghostClass: 'opacity-50',
      chosenClass: 'ring-2 ring-crust-500 ring-offset-2',
      dragClass: 'shadow-lg',
      delay: 100, // Delay before drag starts (better for touch devices)
      delayOnTouchOnly: true, // Only apply delay on touch devices
      touchStartThreshold: 3, // Pixels moved before drag starts on touch

      onEnd: (evt) => {
        log.debug('Image reordered', { oldIndex: evt.oldIndex, newIndex: evt.newIndex })

        // Update uploadedFiles array order
        this.reorderUploadedFiles(evt.oldIndex, evt.newIndex)

        // Update primary badge (first image)
        this.updatePrimaryBadge()

        // Reorder hidden fields to match DOM order
        this.reorderHiddenFields()
      }
    })
  }

  /**
   * Reorder uploadedFiles array to match DOM order
   */
  reorderUploadedFiles(oldIndex, newIndex) {
    if (oldIndex === newIndex) return

    const [movedItem] = this.uploadedFiles.splice(oldIndex, 1)
    this.uploadedFiles.splice(newIndex, 0, movedItem)
  }

  /**
   * Update primary badge - only first image should have it
   */
  updatePrimaryBadge() {
    if (!this.hasPreviewTarget) return

    const previews = this.previewTarget.querySelectorAll('.direct-upload-preview')
    previews.forEach((preview, index) => {
      // Find or create primary badge
      let badge = preview.querySelector('.primary-badge')

      if (index === 0) {
        // First image should have badge
        if (!badge) {
          badge = document.createElement('div')
          badge.className = 'primary-badge absolute bottom-2 left-1/2 -translate-x-1/2 px-1.5 py-0.5 rounded-full bg-charcoal-900/60 backdrop-blur-sm text-white text-xs font-medium whitespace-nowrap z-10'
          badge.textContent = getI18nMessage(
            this.element,
            'primaryImageLabel',
            '主圖'
          )
          preview.appendChild(badge)
        }
      } else {
        // Other images should not have badge
        if (badge) {
          badge.remove()
        }
      }
    })
  }

  /**
   * Reorder hidden fields to match DOM order
   * This ensures the server receives images in the correct order
   */
  reorderHiddenFields() {
    if (!this.hasInputTarget || !this.inputTarget.form) return

    const form = this.inputTarget.form
    const fieldName = this.inputTarget.name

    // Get all hidden fields for this input
    const hiddenFields = Array.from(
      form.querySelectorAll(`input[type="hidden"][name="${fieldName}"]`)
    )

    // Get all preview containers in current order
    const previews = this.previewTarget.querySelectorAll('.direct-upload-preview')

    // Create a map of preview token to hidden field
    const fieldMap = new Map()
    hiddenFields.forEach(field => {
      const previewToken = field.dataset.previewToken
      if (previewToken) {
        fieldMap.set(previewToken, field)
      }
    })

    // Reorder hidden fields to match preview order
    previews.forEach((preview) => {
      const previewToken = preview.dataset.previewToken

      if (previewToken && fieldMap.has(previewToken)) {
        // Move to end (maintains order as we iterate)
        form.appendChild(fieldMap.get(previewToken))
      }
    })

    log.debug('Hidden fields reordered to match preview order')
  }

  // Sync existing images from DOM
  syncExistingImages() {
    if (!this.hasPreviewTarget) return

    const existingPreviews = this.previewTarget.querySelectorAll('.direct-upload-preview')
    existingPreviews.forEach(preview => {
      const removeButton = preview.querySelector('button[data-image-id]')
      const imageId = removeButton?.dataset?.imageId
      const previewToken = preview.dataset.previewToken || this.nextPreviewToken(`existing:${imageId || 'image'}`)
      preview.dataset.previewToken = previewToken

      if (imageId && this.hasInputTarget && this.inputTarget.form) {
        const hiddenField = this.inputTarget.form.querySelector(
          `input[type="hidden"][name="${this.inputTarget.name}"][value="${imageId}"]`
        )
        if (hiddenField) {
          hiddenField.dataset.previewToken = previewToken
        }
      }

      // Existing images don't have blob info, add placeholder
      this.uploadedFiles.push({ existing: true, element: preview })
    })
  }

  nextPreviewToken(prefix = 'upload') {
    if (window.crypto?.randomUUID) {
      return `${prefix}-${window.crypto.randomUUID()}`
    }

    return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
  }

  openSelector(event) {
    event.preventDefault()

    // Android: Use NativeBridge.Camera API (custom implementation)
    // iOS + Web: Use standard file input (iOS shows system native picker)
    if (this.isNative) {
      this.openNativeSelector()
      return
    }

    if (this.hasInputTarget) {
      this.inputTarget.click()
    }
  }

  /**
   * Open native camera/gallery selector (Android only)
   * Uses NativeBridge.Camera.pickMultiple() for image selection
   * Then uploads via NativeBridge.Upload.directUpload() to Active Storage
   * Note: iOS now uses standard file input for system native picker UI
   */
  async openNativeSelector() {
    const currentCount = this.getCurrentImageCount()
    const remaining = this.maxFilesValue - currentCount

    if (remaining <= 0) {
      this.disableAddButton()
      return
    }

    try {
      log.info(`Opening native selector, remaining slots: ${remaining}`)

      // Request images from native camera/gallery
      const result = await NativeBridge.Camera.pickMultiple({
        source: "both",      // Allow camera or gallery selection
        maxCount: remaining,
        maxSize: 1024        // Max dimension in pixels (matches BusinessConstants)
      })

      // Handle result - can be single image or array of images
      const images = result.images || [result]
      log.info(`Native selector returned ${images.length} images`)

      // Upload each selected image
      for (const imageData of images) {
        await this.uploadNativeImage(imageData)
      }
    } catch (error) {
      // User cancelled - not an error
      if (error.message === "User cancelled" || error.message?.includes("cancel")) {
        log.debug("Native image selection cancelled by user")
        return
      }

      // Actual error
      log.error("Native image selection failed", error)
      const errorMsg = getI18nMessage(
        this.element,
        'errorsNativePickerFailed',
        '選擇圖片失敗。請再試一次。'
      )
      showError(errorMsg)
    }
  }

  /**
   * Upload native image to Active Storage via NativeBridge.Upload
   *
   * Flow (Hotwire Native Best Practice - WebView handles authenticated requests):
   * 1. JavaScript gets presigned URL from Rails using fetch() (WebView cookies - always valid)
   * 2. JavaScript passes presigned URL + headers + signed_id to Android
   * 3. Android uploads file directly to storage (no authentication needed)
   *
   * This bypasses Android cookie synchronization issues between WebView and HttpURLConnection.
   *
   * @param {object} imageData - From CameraBridgeHandler
   * @param {string} imageData.uri - Content URI (content:// or file://)
   * @param {string} imageData.previewUri - Base64 data URL for WebView preview (data:image/jpeg;base64,...)
   * @param {string} imageData.filename - Original filename
   * @param {string} imageData.contentType - MIME type (e.g., "image/jpeg")
   * @param {number} imageData.byteSize - File size in bytes
   * @param {string} imageData.checksum - Base64 MD5 checksum
   */
  async uploadNativeImage(imageData) {
    log.debug("Uploading native image", { filename: imageData.filename, size: imageData.byteSize })

    // Create preview container with loading state
    const previewContainer = this.createNativePreviewContainer(imageData)

    try {
      // Step 1: Get presigned URL from Rails using JavaScript fetch()
      // This uses WebView's cookies which are always valid (no sync issues)
      log.info("Getting presigned URL from Rails (JavaScript fetch)")
      const presignedData = await this.getPresignedUrl({
        filename: imageData.filename,
        content_type: imageData.contentType,
        byte_size: imageData.byteSize,
        checksum: imageData.checksum
      })

      log.info("Got presigned URL from Rails", { signed_id: presignedData.signed_id?.substring(0, 20) + "..." })

      // Step 2: Upload via NativeBridge with pre-obtained presigned URL
      // Android uses "optimized mode" - skips Rails request, uploads directly to storage
      const uploadResult = await NativeBridge.Upload.directUpload({
        uri: imageData.uri,
        filename: imageData.filename,
        contentType: imageData.contentType,
        byteSize: imageData.byteSize,
        checksum: imageData.checksum,
        // Optimized mode: Pass presigned URL to Android (bypasses cookie sync issues)
        url: presignedData.url,
        headers: presignedData.headers,
        signed_id: presignedData.signed_id
      })

      log.info("Native upload completed", { signed_id: uploadResult.signed_id })

      // Create blob-like object for compatibility with existing code
      const blob = {
        signed_id: uploadResult.signed_id,
        filename: imageData.filename,
        content_type: imageData.contentType,
        byte_size: imageData.byteSize
      }

      this.uploadedFiles.push(blob)
      this.showSuccess(previewContainer, blob)
      this.createHiddenField(previewContainer, blob)

    } catch (error) {
      // Log detailed error information for debugging
      log.error("Native upload failed", {
        message: error.message,
        filename: imageData.filename,
        byteSize: imageData.byteSize
      })

      // Provide diagnostic information based on error source
      const errorMsg = error.message || ""

      // JavaScript fetch errors (presigned URL request)
      if (errorMsg.includes("Presigned URL failed")) {
        if (errorMsg.includes("401")) {
          log.warn("Session expired - user needs to re-login")
        } else if (errorMsg.includes("403")) {
          log.warn("Permission denied - user may not have access")
        } else if (errorMsg.includes("422")) {
          log.warn("Invalid file parameters - check filename/content_type")
        }
      }
      // Android upload errors (storage upload)
      else if (errorMsg.includes("STORAGE_FAILED")) {
        log.warn("Cloud storage upload failed - may be temporary")
      } else if (errorMsg.includes("NETWORK_ERROR")) {
        log.warn("Network error - check internet connection")
      }

      this.showError(previewContainer, error)
    }
  }

  /**
   * Get presigned URL from Rails using JavaScript fetch
   *
   * Uses WebView's cookies which are always valid (no Android cookie sync issues).
   * This is the Hotwire Native best practice: web handles authenticated requests,
   * native handles file operations.
   *
   * @param {object} blobData - File metadata
   * @param {string} blobData.filename - Original filename
   * @param {string} blobData.content_type - MIME type
   * @param {number} blobData.byte_size - File size in bytes
   * @param {string} blobData.checksum - Base64 MD5 checksum
   * @returns {Promise<{url: string, headers: object, signed_id: string}>}
   */
  async getPresignedUrl(blobData) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    const response = await fetch('/api/native/direct_uploads', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify(blobData),
      credentials: 'same-origin'  // Include session cookies
    })

    if (!response.ok) {
      const errorText = await response.text().catch(() => '')
      throw new Error(`Presigned URL failed: HTTP ${response.status} - ${errorText}`)
    }

    const result = await response.json()
    if (!result.success) {
      throw new Error(result.error || 'Failed to get presigned URL')
    }

    return result.direct_upload
  }

  /**
   * Create preview container for native image
   * Uses imageData.uri for preview (content:// URI can be displayed by Android WebView)
   */
  createNativePreviewContainer(imageData) {
    const container = document.createElement("div")
    container.className = "relative aspect-square rounded-xl border border-charcoal-100 overflow-hidden direct-upload-preview"
    container.dataset.previewToken = this.nextPreviewToken('native')

    // Create image preview using base64 data URL
    // WebView cannot render content:// URIs (lacks ContentProvider permission)
    // Use previewUri (base64 data URL) from CameraBridgeHandler for reliable rendering
    const img = document.createElement("img")
    img.className = "h-full w-full object-cover"
    // Prefer previewUri (base64), fall back to uri for backward compatibility
    img.src = imageData.previewUri || imageData.uri
    img.alt = imageData.filename
    // Fallback: If preview fails (unlikely with base64), show placeholder
    img.onerror = () => {
      img.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23ccc'%3E%3Cpath d='M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z'/%3E%3C/svg%3E"
    }
    container.appendChild(img)

    // Create delete button
    const removeButton = document.createElement("button")
    removeButton.type = "button"
    removeButton.className = "absolute top-2 left-2 flex h-6 w-6 items-center justify-center rounded-full bg-red-600 text-white shadow-sm active:scale-[0.97] transition-all duration-200 z-10"
    removeButton.innerHTML = this.removeButtonSVG
    removeButton.addEventListener('click', () => {
      // Upload might be in progress
      if (container.querySelector('[data-direct-upload-target="progress"]')) {
        const confirmMsg = getI18nMessage(
          this.element,
          'confirmCancelUpload',
          '上傳進行中。確定要取消嗎？'
        )
        if (!confirm(confirmMsg)) {
          return
        }
      }
      this.removePreview(container, { signed_id: null }) // Will be updated after upload
    })
    container.appendChild(removeButton)

    // Add primary image badge for first image
    const isFirstImage = this.getCurrentImageCount() === 0
    if (isFirstImage) {
      const primaryBadge = document.createElement("div")
      primaryBadge.className = "absolute bottom-2 left-1/2 -translate-x-1/2 px-1.5 py-0.5 rounded-full bg-charcoal-900/60 backdrop-blur-sm text-white text-xs font-medium whitespace-nowrap z-10"
      primaryBadge.textContent = "主圖"
      container.appendChild(primaryBadge)
    }

    // Create progress overlay (same as createPreviewContainer)
    const progressOverlay = document.createElement("div")
    progressOverlay.className = "absolute inset-0 bg-charcoal-900/50 flex items-center justify-center"
    progressOverlay.dataset.directUploadTarget = "progress"

    const progressBar = document.createElement("div")
    progressBar.className = "w-3/4 h-2 rounded-full bg-charcoal-700 overflow-hidden"

    const progressFill = document.createElement("div")
    progressFill.className = "h-full bg-crust-600 transition-all duration-300"
    progressFill.style.width = "0%"
    progressFill.dataset.progressBar = ""

    progressBar.appendChild(progressFill)
    progressOverlay.appendChild(progressBar)
    container.appendChild(progressOverlay)

    // Simulate progress for native upload (native side doesn't provide progress events)
    this.simulateNativeProgress(progressFill)

    // Add to preview area
    if (this.hasPreviewTarget) {
      this.previewTarget.appendChild(container)
    }

    return container
  }

  /**
   * Simulate progress for native upload (native HTTP client doesn't provide progress events)
   * Uses smooth animation from 0% to 90%, final jump to 100% on completion
   */
  simulateNativeProgress(progressFill) {
    let progress = 0
    const interval = setInterval(() => {
      if (progress < 90) {
        progress += Math.random() * 15
        progress = Math.min(progress, 90)
        progressFill.style.width = `${progress}%`
      } else {
        clearInterval(interval)
      }
    }, 200)

    // Store interval ID for cleanup
    progressFill.dataset.intervalId = interval
  }

  // SVG template for remove button (Single Source of Truth)
  get removeButtonSVG() {
    return `
      <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
      </svg>
    `
  }

  // Handle file selection
  selectFiles(event) {
    const files = Array.from(event.target.files)

    // Validate file types and size FIRST
    const allowedFormats = this.allowedFormatsValue.split(',')
    const formatRegex = new RegExp(`^image/(${allowedFormats.join('|')})$`, 'i')

    const validFiles = files.filter(file => {
      // Check file type (from BusinessConstants)
      if (!file.type.match(formatRegex)) {
        const errorMsg = getI18nMessage(
          this.element,
          'errorsUnsupportedFormat',
          '%{name} 檔案格式不支援。',
          { name: file.name }
        )
        showError(errorMsg)
        return false
      }

      // Check file size (from BusinessConstants)
      if (file.size > this.maxSizeValue) {
        const maxMB = Math.round(this.maxSizeValue / (1024 * 1024))
        const errorMsg = getI18nMessage(
          this.element,
          'errorsFileSizeExceeded',
          '%{name} 檔案超過 %{size}MB 限制。',
          { name: file.name, size: maxMB }
        )
        showError(errorMsg)
        return false
      }

      return true
    })

    if (validFiles.length === 0) {
      event.target.value = ""  // Always clear to allow re-selection
      return
    }

    // ✅ DOM-based count (includes in-flight uploads)
    const currentCount = this.getCurrentImageCount()
    const newCount = currentCount + validFiles.length

    if (newCount > this.maxFilesValue) {
      const remaining = this.maxFilesValue - currentCount
      const errorMsg = getI18nMessage(
        this.element,
        'errorsMaxFilesExceeded',
        '最多可上傳 %{max} 張圖片。\n（目前 %{current} 張，還可新增 %{remaining} 張）',
        { max: this.maxFilesValue, current: currentCount, remaining: remaining }
      )
      showError(errorMsg)
      event.target.value = ""  // Always clear to allow re-selection
      return
    }

    // Upload each file (cumulative, no clearPreviews())
    validFiles.forEach(file => this.uploadFile(file))

    // Always clear input to allow re-selection of same files
    event.target.value = ""
  }

  // Upload single file using Active Storage Direct Upload
  uploadFile(file) {
    const upload = new DirectUpload(file, this.urlValue, this)

    // Create preview container
    const previewContainer = this.createPreviewContainer(file)

    // Store container for directUploadWillStoreFileWithXHR to access
    this._currentContainer = previewContainer

    // Start upload
    upload.create((error, blob) => {
      // Check if upload was cancelled (container removed)
      if (previewContainer.dataset.cancelled === 'true') {
        log.debug('Upload cancelled, ignoring callback')
        return
      }

      if (error) {
        log.error('Upload failed', error)
        this.showError(previewContainer, error)
      } else {
        this.uploadedFiles.push(blob)
        this.showSuccess(previewContainer, blob)
        this.createHiddenField(previewContainer, blob)
      }
      // Clean up reference after upload completes
      this._currentContainer = null
    })
  }

  // Create preview container for each image
  createPreviewContainer(file) {
    const container = document.createElement("div")
    container.className = "relative aspect-square rounded-xl border border-charcoal-100 overflow-hidden direct-upload-preview"
    container.dataset.previewToken = this.nextPreviewToken('web')

    // Create image preview
    const img = document.createElement("img")
    img.className = "h-full w-full object-cover"
    img.alt = file.name

    // Use FileReader for WebView compatibility (blob URLs don't work in Android WebView)
    // blob:// URLs are JavaScript memory references that WebView's separate rendering process cannot access
    const reader = new FileReader()
    reader.onload = (e) => {
      img.src = e.target.result  // data:image/...;base64,...
    }
    reader.readAsDataURL(file)

    container.appendChild(img)

    // Create delete button (initially bound to file) - Mobile optimized
    const removeButton = document.createElement("button")
    removeButton.type = "button"
    removeButton.className = "absolute top-2 left-2 flex h-6 w-6 items-center justify-center rounded-full bg-red-600 text-white shadow-sm active:scale-[0.97] transition-all duration-200 z-10"
    removeButton.innerHTML = this.removeButtonSVG
    // Use addEventListener for CSP compliance
    removeButton.addEventListener('click', () => {
      // Check if upload is in progress
      if (container.querySelector('[data-direct-upload-target="progress"]')) {
        const confirmMsg = getI18nMessage(
          this.element,
          'confirmCancelUpload',
          '上傳進行中。確定要取消嗎？'
        )
        if (!confirm(confirmMsg)) {
          return
        }
      }
      this.removePreview(container, file)
    })
    container.appendChild(removeButton)

    // Add primary image badge for first image
    const isFirstImage = this.getCurrentImageCount() === 0
    if (isFirstImage) {
      const primaryBadge = document.createElement("div")
      primaryBadge.className = "absolute bottom-2 left-1/2 -translate-x-1/2 px-1.5 py-0.5 rounded-full bg-charcoal-900/60 backdrop-blur-sm text-white text-xs font-medium whitespace-nowrap z-10"
      primaryBadge.textContent = "主圖" // TODO: Use I18n when available in JavaScript (products.form.primary_image_badge)
      container.appendChild(primaryBadge)
    }

    // Create progress overlay
    const progressOverlay = document.createElement("div")
    progressOverlay.className = "absolute inset-0 bg-charcoal-900/50 flex items-center justify-center"
    progressOverlay.dataset.directUploadTarget = "progress"

    const progressBar = document.createElement("div")
    progressBar.className = "w-3/4 h-2 rounded-full bg-charcoal-700 overflow-hidden"

    const progressFill = document.createElement("div")
    progressFill.className = "h-full bg-crust-600 transition-all duration-300"
    progressFill.style.width = "0%"
    progressFill.dataset.progressBar = ""

    progressBar.appendChild(progressFill)
    progressOverlay.appendChild(progressBar)
    container.appendChild(progressOverlay)

    // Add to preview area
    if (this.hasPreviewTarget) {
      this.previewTarget.appendChild(container)
    }

    return container
  }

  // DirectUpload callback: track progress
  directUploadWillStoreFileWithXHR(request) {
    // Get the container stored in uploadFile
    const container = this._currentContainer

    request.upload.addEventListener("progress", event => {
      const progress = (event.loaded / event.total) * 100
      // Find progress bar in the stored container (not event.target)
      const progressBar = container?.querySelector("[data-progress-bar]")
      if (progressBar) {
        progressBar.style.width = `${progress}%`
      }
    })
  }

  // Show success state
  showSuccess(container, blob) {
    // Finish the bar, fade the overlay away and let the thumbnail settle, instead of a hard cut.
    const progressOverlay = container.querySelector("[data-direct-upload-target='progress']")
    if (progressOverlay) {
      const fill = progressOverlay.querySelector("[data-progress-bar]")
      if (fill) fill.style.width = "100%"
      progressOverlay.dataset.directUploadTarget = "" // no longer "uploading"
      if (typeof progressOverlay.animate === "function" && !prefersReducedMotion()) {
        progressOverlay.animate([ { opacity: 1 }, { opacity: 0 } ], { duration: 220, delay: 120, easing: "ease-out", fill: "forwards" })
          .finished.then(() => progressOverlay.remove(), () => progressOverlay.remove())
        container.animate([ { transform: "scale(0.96)" }, { transform: "scale(1)" } ], { duration: 320, delay: 120, easing: "cubic-bezier(0.23, 1, 0.32, 1)" })
      } else {
        progressOverlay.remove()
      }
    }

    // Update delete button click handler to use blob instead of file
    const removeButton = container.querySelector('button')
    if (removeButton) {
      // Remove all existing click listeners by cloning and replacing
      const newButton = removeButton.cloneNode(true)
      removeButton.parentNode.replaceChild(newButton, removeButton)
      newButton.dataset.imageId = blob.signed_id
      // Add new listener with blob reference
      newButton.addEventListener('click', () => {
        this.removePreview(container, blob)
      })
    }

    // Add success indicator with animation
    const successBadge = document.createElement("div")
    successBadge.className = "absolute top-2 right-2 flex h-6 w-6 items-center justify-center rounded-full bg-green-600 shadow-sm animate-success-pop"
    successBadge.innerHTML = `
      <svg class="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path class="upload-check" pathLength="1" stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/>
      </svg>
    `
    container.appendChild(successBadge)
    this.updateCount()

    // ✅ DOM-based count check
    if (this.getCurrentImageCount() >= this.maxFilesValue) {
      this.disableAddButton()
    }
  }

  // Show error state
  showError(container, error) {
    const progressOverlay = container.querySelector("[data-direct-upload-target='progress']")
    if (progressOverlay) {
      progressOverlay.remove()
    }

    // Extract error message and code
    const errorMessage = error?.message || error?.code || 'UNKNOWN'
    log.error("Upload error displayed", { message: errorMessage })

    // Extract HTTP code from error message (e.g., "HTTP 401", "Server returned HTTP 403")
    const httpMatch = errorMessage.match(/(?:HTTP[_ ]?|Status:\s*)(\d{3})/i)
    const httpCode = httpMatch ? httpMatch[1] : null

    // User-friendly error messages based on HTTP code (Traditional Chinese)
    const httpErrorMessages = {
      '401': '登入已過期，請重新登入',
      '403': '權限不足，無法上傳',
      '404': '上傳服務暫時無法使用',
      '413': '檔案太大，請選擇較小的圖片',
      '422': '檔案格式錯誤',
      '500': '伺服器錯誤，請稍後再試',
      '502': '伺服器暫時無法使用',
      '503': '服務維護中，請稍後再試'
    }

    // Determine user-facing message
    let userMessage
    if (httpCode && httpErrorMessages[httpCode]) {
      userMessage = httpErrorMessages[httpCode]
    } else if (errorMessage.includes('CANCELLED') || errorMessage.includes('cancelled')) {
      userMessage = getI18nMessage(this.element, 'statusUploadCancelled', '已取消')
    } else if (errorMessage.includes('NETWORK_ERROR') || errorMessage.includes('Network')) {
      userMessage = '網路錯誤，請檢查連線'
    } else if (errorMessage.includes('STORAGE_FAILED')) {
      userMessage = '儲存失敗，請稍後再試'
    } else {
      userMessage = getI18nMessage(this.element, 'statusUploadFailed', '上傳失敗')
    }

    // Add error indicator with user-friendly message
    const errorBadge = document.createElement("div")
    errorBadge.className = "absolute inset-0 flex items-center justify-center bg-red-900/80"
    errorBadge.innerHTML = `
      <div class="text-center text-white">
        <svg class="h-8 w-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
        <p class="text-xs">${userMessage}</p>
        ${httpCode ? `<p class="text-2xs opacity-70 mt-1">HTTP ${httpCode}</p>` : ''}
      </div>
    `
    container.appendChild(errorBadge)
    this.updateCount()
  }

  // Create hidden field for blob signed_id
  createHiddenField(container, blob) {
    // Double-check: container might be cancelled/removed
    if (container.dataset.cancelled === 'true') {
      log.debug('Container cancelled, skipping hidden field creation')
      return
    }

    const hiddenField = document.createElement("input")
    hiddenField.type = "hidden"
    hiddenField.name = `${this.inputTarget.name}`
    hiddenField.value = blob.signed_id
    hiddenField.dataset.previewToken = container.dataset.previewToken
    this.inputTarget.form.appendChild(hiddenField)
    this.reorderHiddenFields()
  }

  // Disable add button when max files reached
  disableAddButton() {
    if (this.hasAddButtonTarget) {
      this.addButtonTarget.disabled = true
      this.addButtonTarget.classList.add('opacity-50', 'cursor-not-allowed', 'pointer-events-none')
    }
  }

  // Enable add button
  enableAddButton() {
    if (this.hasAddButtonTarget) {
      this.addButtonTarget.disabled = false
      this.addButtonTarget.classList.remove('opacity-50', 'cursor-not-allowed', 'pointer-events-none')
    }
  }

  // Clear all previews
  clearPreviews() {
    if (this.hasPreviewTarget) {
      this.previewTarget.innerHTML = ""
    }
    this.uploadedFiles = []

    // Remove previous hidden fields (images)
    const hiddenFields = this.inputTarget.form.querySelectorAll(`input[type="hidden"][name="${this.inputTarget.name}"]`)
    hiddenFields.forEach(field => field.remove())

    // ✅ Remove deletion markers (images_to_delete)
    const deletionMarkers = this.inputTarget.form.querySelectorAll('input[type="hidden"][name="product[images_to_delete][]"]')
    deletionMarkers.forEach(field => field.remove())

    this.inputTarget.value = ""
    this.updateCount()

    // Enable button (reset to initial state)
    this.enableAddButton()
  }

  // Remove a single preview (works for both new uploads and existing images)
  removePreview(container, fileOrBlob) {
    // Mark container as cancelled (prevents callback from executing)
    container.dataset.cancelled = 'true'

    // Remove from uploadedFiles array
    const index = this.uploadedFiles.findIndex(item => {
      // Handle existing images: {existing: true, element: container}
      if (item.existing && item.element === container) {
        return true
      }
      // Handle new uploads: blob object
      if (item.signed_id === fileOrBlob.signed_id) {
        return true
      }
      return false
    })

    if (index !== -1) {
      this.uploadedFiles.splice(index, 1)
    }

    // Remove corresponding hidden field (for blob objects)
    if (fileOrBlob.signed_id) {
      const hiddenField = this.inputTarget.form.querySelector(
        `input[type="hidden"][name="${this.inputTarget.name}"][value="${fileOrBlob.signed_id}"]`
      )
      if (hiddenField) {
        hiddenField.remove()
      }
    }

    // Remove container from DOM
    container.remove()
    this.updatePrimaryBadge()
    this.reorderHiddenFields()

    // Update count and re-enable button if under max
    this.updateCount()
    // ✅ DOM-based count check
    if (this.getCurrentImageCount() < this.maxFilesValue) {
      this.enableAddButton()
    }
  }

  // Remove existing image (server-rendered in edit mode)
  removeExistingImage(event) {
    try {
      event.preventDefault()
      const button = event.currentTarget
      const container = button.closest('.direct-upload-preview')
      const imageId = button.dataset.imageId

      if (!container) {
        log.error('找不到圖片容器')
        return
      }
      if (!imageId) {
        log.error('找不到圖片 ID')
        return
      }
      if (!this.inputTarget?.form) {
        log.error('找不到表單')
        return
      }

      // Remove the hidden field that preserves this existing image
      const preservationField = this.inputTarget.form.querySelector(
        `input[type="hidden"][name="${this.inputTarget.name}"][value="${imageId}"][data-existing-image="true"]`
      )
      if (preservationField) {
        preservationField.remove()
        log.info('已移除現有圖片保留欄位:', imageId)
      }

      // Create hidden deletion marker field (Rails will process this on form submit)
      const deletionField = document.createElement("input")
      deletionField.type = "hidden"
      deletionField.name = `${this.modelNameValue}[images_to_delete][]`
      deletionField.value = imageId
      this.inputTarget.form.appendChild(deletionField)

      // Remove from uploadedFiles array
      const index = this.uploadedFiles.findIndex(item => item.existing && item.element === container)
      if (index !== -1) {
        this.uploadedFiles.splice(index, 1)
      }

      // Remove container from DOM
      container.remove()
      this.updatePrimaryBadge()
      this.reorderHiddenFields()

      // Update count and re-enable button if under max
      this.updateCount()
      // ✅ DOM-based count check
      if (this.getCurrentImageCount() < this.maxFilesValue) {
        this.enableAddButton()
      }
    } catch (error) {
      log.error('刪除圖片時發生錯誤', error)
    }
  }

  // Get current image count from DOM (Single Source of Truth)
  getCurrentImageCount() {
    if (!this.hasPreviewTarget) return 0
    return this.previewTarget.querySelectorAll(".direct-upload-preview, [data-direct-upload='preview']").length
  }

  updateCount() {
    if (!this.hasCountTarget) return
    const currentCount = this.getCurrentImageCount()
    const max = this.maxFilesValue || 0
    this.countTarget.textContent = `${currentCount}/${max}`
  }

  // ============================================
  // Dropzone Style Support (Listing Wizard)
  // Click-only upload, no drag-and-drop
  // ============================================

  // Alias for openSelector (Dropzone click handler)
  triggerFileInput(event) {
    this.openSelector(event)
  }

  // Alias for selectFiles (File input change handler)
  handleFileSelect(event) {
    this.selectFiles(event)
  }

  // Alias for removeExistingImage
  removeExisting(event) {
    this.removeExistingImage(event)
  }
}
